import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Button,
  Heading,
  Spinner,
  SimpleGrid,
} from '@chakra-ui/react';
import { GoogleAuthProvider, signInWithPopup, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import { initializePaddle } from '@paddle/paddle-js';
import { PADDLE_ENVIRONMENT, PADDLE_TOKEN } from '../paddleConfig';

const FREE_EMAIL_LIMIT = 1000;

// Temporarily bypass the sign-in requirement so Subscribe goes straight to
// the Paddle checkout. Set back to true in a couple of days to re-enable the
// login gate.
const REQUIRE_LOGIN = false;

const PRICE_IDS = [
  'pri_01krknn0b3hd83gn86zjj41m0b', // monthly
  'pri_01krknqxbt3q4p8t11h1901nhn', // annual
];

const FREE_FEATURES = [
  'Auto-suggests rules for new senders',
  'Background cleanup',
  'All data stays in your browser',
  'No credit card needed',
];

const UNLIMITED_EXTRA = 'Unlimited cleanup — no monthly caps';

const FAQ_ITEMS = [
  {
    question: `What counts toward the ${FREE_EMAIL_LIMIT}-email limit?`,
    answer: "Only emails Email Cleaner actually removes for you. Emails you read, send, or that don't match any rule are free.",
  },
  {
    question: 'What if I go over on the Free plan?',
    answer: "Cleanup pauses until the 1st of next month. Nothing breaks, nothing's lost — your inbox just goes back to fending for itself for a few days.",
  },
  {
    question: 'Is this safe? Can you see my emails?',
    answer: 'No. Email Cleaner is a browser extension — it talks to Gmail directly using Google\'s official login. Your emails never pass through us. There is no "us" to pass through.',
  },
  {
    question: 'Can I cancel anytime?',
    answer: "Yes. One click in your account settings — no hoops, no guilt trips, no \"are you sure?\" emails.",
  },
  {
    question: 'Will I lose important emails?',
    answer: "You make the rules. You can pause any rule, edit it, or undo it. Nothing gets deleted that you didn't decide to delete.",
  },
];

const CheckIcon = () => (
  <Text as="span" color="#52c41a" fontWeight="bold" lineHeight="1.6" flexShrink={0}>
    &#10003;
  </Text>
);

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

const Pricing = () => {
  const [user, setUser] = useState(null);
  const [paddle, setPaddle] = useState(null);
  const [prices, setPrices] = useState([]);
  const [pricesLoading, setPricesLoading] = useState(false);
  const [selectedPriceIdx, setSelectedPriceIdx] = useState(0);
  const [signingIn, setSigningIn] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelPriceId, setPanelPriceId] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
    });
    return unsubscribe;
  }, []);

  const loadPrices = useCallback(async (paddleInstance) => {
    setPricesLoading(true);
    try {
      const result = await paddleInstance.PricePreview({
        items: PRICE_IDS.map(id => ({ priceId: id, quantity: 1 })),
      });
      setPrices(result.data.details.lineItems);
    } catch (err) {
      console.error('Failed to load prices:', err);
    } finally {
      setPricesLoading(false);
    }
  }, []);

  useEffect(() => {
    initializePaddle({
      environment: PADDLE_ENVIRONMENT,
      token: PADDLE_TOKEN,
    }).then((paddleInstance) => {
      if (paddleInstance) {
        setPaddle(paddleInstance);
        loadPrices(paddleInstance);
      }
    });
  }, [loadPrices]);

  const openCheckout = useCallback((priceId, currentUser) => {
    if (!paddle) return;
    if (REQUIRE_LOGIN && !currentUser) return;
    paddle.Checkout.open({
      items: [{ priceId, quantity: 1 }],
      ...(currentUser
        ? {
            customer: { email: currentUser.email },
            customData: { firebase_user_uid: currentUser.uid },
          }
        : {}),
      settings: {
        displayMode: 'overlay',
        theme: 'light',
      },
    });
  }, [paddle]);

  const handleSubscribe = () => {
    const priceId = prices[selectedPriceIdx]?.price?.id;
    if (!priceId) return;
    setPanelPriceId(priceId);
    if (!REQUIRE_LOGIN || user) {
      openCheckout(priceId, user);
    } else {
      setPanelOpen(true);
    }
  };

  const handleSignIn = async () => {
    setSigningIn(true);
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
    } catch (err) {
      console.error('Sign-in error:', err.message);
    } finally {
      setSigningIn(false);
    }
  };

  // After sign-in, automatically open checkout
  useEffect(() => {
    if (user && panelPriceId && panelOpen) {
      openCheckout(panelPriceId, user);
    }
  }, [user, panelPriceId, panelOpen, openCheckout]);

  return (
    <Box as="main" minH="100vh" bg="gray.50">
      {/* Plans section */}
      <Box>
          <Box pt={{ base: 14, lg: 20 }} pb={{ base: 10, lg: 14 }} />
          <Container maxW="container.md" pb={{ base: 12, lg: 16 }}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} alignItems="stretch">

          {/* Free card */}
          <Box
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="lg"
            p={{ base: 6, md: 8 }}
            display="flex"
            flexDirection="column"
          >
            <VStack spacing={6} align="stretch" flex={1}>
              <Box minH="70px">
                <Text fontSize="sm" fontWeight="medium" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={2}>
                  Free
                </Text>
              </Box>

              <VStack spacing={3} align="stretch">
                <HStack spacing={3} align="flex-start">
                  <CheckIcon />
                  <Text fontSize="sm" color="gray.600" lineHeight="1.6" fontWeight="bold">
                    {FREE_EMAIL_LIMIT} emails / month
                  </Text>
                </HStack>
                {FREE_FEATURES.map((feature) => (
                  <HStack key={feature} spacing={3} align="flex-start">
                    <CheckIcon />
                    <Text fontSize="sm" color="gray.600" lineHeight="1.6">
                      {feature}
                    </Text>
                  </HStack>
                ))}
              </VStack>
            </VStack>
          </Box>

          {/* Unlimited card */}
          <Box
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            borderTop="3px solid #52c41a"
            borderRadius="lg"
            p={{ base: 6, md: 8 }}
            position="relative"
            display="flex"
            flexDirection="column"
          >
            {/* Billing toggle — top right */}
            {!pricesLoading && prices.length > 0 && (
              <HStack spacing={1} position="absolute" top={4} right={4}>
                {prices.map((item, idx) => {
                  const label = item.price.billingCycle?.interval === 'month' ? 'Monthly' : 'Yearly';
                  const isSelected = selectedPriceIdx === idx;
                  return (
                    <Button
                      key={item.price.id}
                      onClick={() => setSelectedPriceIdx(idx)}
                      size="xs"
                      variant={isSelected ? 'solid' : 'ghost'}
                      bg={isSelected ? '#52c41a' : 'transparent'}
                      color={isSelected ? 'white' : 'gray.500'}
                      _hover={{
                        bg: isSelected ? '#389e0d' : 'gray.100',
                      }}
                      fontWeight="medium"
                      fontSize="xs"
                      minW="auto"
                      px={2}
                      h="24px"
                    >
                      {label}
                    </Button>
                  );
                })}
              </HStack>
            )}

            <VStack spacing={6} align="stretch" flex={1}>
              <Box minH="70px">
                <Text fontSize="sm" fontWeight="medium" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={2}>
                  Unlimited
                </Text>

                {pricesLoading ? (
                  <HStack py={2} spacing={3}>
                    <Spinner size="sm" color="#52c41a" />
                    <Text fontSize="sm" color="gray.400">Loading price...</Text>
                  </HStack>
                ) : prices.length > 0 ? (
                  <Box>
                    <HStack align="baseline" spacing={1}>
                      <Text fontSize="4xl" fontWeight="bold" color="gray.800" lineHeight="1">
                        {(() => {
                          const item = prices[selectedPriceIdx];
                          const total = Number(item.totals.total) / 100;
                          const monthly = item.price.billingCycle?.interval === 'year' ? total / 12 : total;
                          return `$${monthly % 1 === 0 ? monthly.toFixed(0) : monthly.toFixed(2)}`;
                        })()}
                      </Text>
                      <Text fontSize="sm" color="gray.400">/ month</Text>
                    </HStack>
                  </Box>
                ) : (
                  <Text fontSize="sm" color="gray.400" mt={2}>Pricing unavailable</Text>
                )}
              </Box>


              <HStack spacing={3} align="flex-start">
                <CheckIcon />
                <Text fontSize="sm" color="#135200" fontWeight="medium" lineHeight="1.6">
                  {UNLIMITED_EXTRA}
                </Text>
              </HStack>

              <Box flex={1} />

              <Button
                onClick={handleSubscribe}
                bg="#52c41a"
                color="white"
                _hover={{ bg: '#389e0d' }}
                size="md"
                isDisabled={pricesLoading || prices.length === 0}
              >
                Subscribe
              </Button>
            </VStack>
          </Box>

            </SimpleGrid>
          </Container>
        </Box>

        {/* Overlay dialog */}
        {panelOpen && (
          <>
          <Box
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="blackAlpha.500"
            zIndex={1000}
            onClick={() => setPanelOpen(false)}
          />
          <Box
            position="fixed"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            w={{ base: '90%', sm: '800px' }}
            bg="white"
            borderRadius="xl"
            boxShadow="xl"
            p={8}
            zIndex={1001}
          >
          <Box
            as="button"
            position="absolute"
            top={3}
            right={3}
            w="32px"
            h="32px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="full"
            bg="transparent"
            color="gray.400"
            fontSize="lg"
            _hover={{ bg: 'gray.100', color: 'gray.600' }}
            onClick={() => setPanelOpen(false)}
          >
            &#10005;
          </Box>
          <VStack spacing={8} align="stretch">
            {/* Step 1 */}
            <HStack align="flex-start" spacing={4}>
              <Box
                w="36px"
                h="36px"
                borderRadius="full"
                bg={user ? '#52c41a' : 'gray.100'}
                color={user ? 'white' : 'gray.600'}
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontWeight="bold"
                fontSize="sm"
                flexShrink={0}
              >
                {user ? '✓' : '1'}
              </Box>
              <Box flex={1}>
                <Text fontWeight="semibold" color={user ? 'gray.400' : 'gray.700'} mb={1}>
                  Sign in with Google
                </Text>
                <Text fontSize="sm" color={user ? 'gray.400' : 'gray.500'} mb={3}>
                  {user
                    ? `Signed in as ${user.email}`
                    : 'Log in with your Gmail account so we can bind your subscription to it.'}
                </Text>
                {!user && (
                  <Box
                    as="button"
                    onClick={!signingIn ? handleSignIn : undefined}
                    bg="#fff"
                    border="1px solid #D1D5DB"
                    borderRadius="6px"
                    px="14px"
                    py="8px"
                    transition="all 0.2s"
                    _hover={{ bg: '#F9FAFB' }}
                    opacity={signingIn ? 0.6 : 1}
                    cursor={signingIn ? 'not-allowed' : 'pointer'}
                  >
                    <HStack spacing="10px" justify="center">
                      {signingIn ? (
                        <Spinner size="sm" color="#374151" />
                      ) : (
                        <GoogleIcon />
                      )}
                      <Text color="#374151" fontSize="sm" fontWeight="500">
                        {signingIn ? 'Signing in...' : 'Sign in with Google'}
                      </Text>
                    </HStack>
                  </Box>
                )}
              </Box>
            </HStack>

            {/* Step 2 */}
            <HStack align="flex-start" spacing={4}>
              <Box
                w="36px"
                h="36px"
                borderRadius="full"
                bg={user ? 'gray.100' : 'gray.50'}
                color={user ? 'gray.600' : 'gray.400'}
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontWeight="bold"
                fontSize="sm"
                flexShrink={0}
              >
                2
              </Box>
              <Box flex={1}>
                <Text fontWeight="semibold" color={user ? 'gray.700' : 'gray.400'} mb={1}>
                  Complete your subscription
                </Text>
                <Text fontSize="sm" color={user ? 'gray.500' : 'gray.400'} mb={3}>
                  {user
                    ? 'Payment window is open. If you closed it, click Subscribe again.'
                    : 'Sign in first to continue.'}
                </Text>
                <Box
                  as="button"
                  onClick={user ? handleSubscribe : undefined}
                  bg={user ? '#52c41a' : '#52c41a'}
                  border="1px solid"
                  borderColor={user ? '#52c41a' : '#52c41a'}
                  borderRadius="6px"
                  px="14px"
                  py="8px"
                  opacity={user ? 1 : 0.4}
                  cursor={user ? 'pointer' : 'not-allowed'}
                  _hover={user ? { bg: '#389e0d' } : {}}
                  transition="all 0.2s"
                >
                  <Text color="white" fontSize="sm" fontWeight="500">
                    Subscribe
                  </Text>
                </Box>
              </Box>
            </HStack>
          </VStack>
        </Box>
        </>
      )}

      {/* FAQ */}
      <Box borderTop="1px solid" borderColor="gray.200" bg="white" py={{ base: 12, lg: 16 }}>
        <Container maxW="container.md">
          <Heading
            size="md"
            color="#135200"
            fontWeight="normal"
            mb={8}
          >
            Questions you probably have
          </Heading>
          <VStack spacing={7} align="stretch">
            {FAQ_ITEMS.map((item) => (
              <Box key={item.question}>
                <Text fontWeight="semibold" color="gray.700" mb={1} fontSize="sm">
                  {item.question}
                </Text>
                <Text color="gray.500" lineHeight="relaxed" fontSize="sm">
                  {item.answer}
                </Text>
              </Box>
            ))}
          </VStack>
        </Container>
      </Box>
    </Box>
  );
};

export default Pricing;
